import { useCallback, useEffect, useState } from "react";
import { k, readJSON, writeJSON } from "@/lib/storage";

export type ReadingSize = "S" | "M" | "L" | "XL";
export type AudioVoice = "en-US" | "en-GB";
/**
 * Where pronunciation comes from. `youdao` streams real UK / US recordings
 * from the dictionary (two buttons per word); `browser` uses the device's
 * built-in speech synthesis (one button); `off` hides audio buttons entirely.
 */
export type AudioSource = "youdao" | "browser" | "off";

export interface Settings {
  readingSize: ReadingSize;
  audioSource: AudioSource;
  /** Voice used in `browser` mode. Ignored by the online dictionary source. */
  audio: AudioVoice;
  dailyGoal: number;
  autoPlayOnFlip: boolean;
  /**
   * In Quiz / Typing / Cloze, advance to the next question automatically
   * after a brief delay. When false, the user clicks "Next →" themselves —
   * lets them read the example or feedback at their own pace.
   */
  autoNext: boolean;
  /**
   * How many questions a Quiz / Typing / Cloze round draws from the day's
   * words. Capped at the day's usable pool, so a day with fewer entries than
   * this just uses everything available.
   */
  roundSize: number;
  showIPA: boolean;
  shuffleFlashcards: boolean;
}

const DEFAULTS: Settings = {
  readingSize: "M",
  audioSource: "youdao",
  audio: "en-US",
  dailyGoal: 40,
  autoPlayOnFlip: false,
  autoNext: false,
  roundSize: 10,
  showIPA: true,
  shuffleFlashcards: true,
};

const KEY = k("settings.v1");

const listeners = new Set<() => void>();

/**
 * `audio` used to carry an "off" value before `audioSource` existed. Anyone
 * who had muted audio that way keeps their choice instead of silently getting
 * sound back on this release.
 */
function load(): Settings {
  const stored = readJSON<Omit<Partial<Settings>, "audio"> & { audio?: string }>(KEY, {});
  const merged = { ...DEFAULTS, ...stored } as Settings;
  if (stored.audio === "off") {
    merged.audioSource = "off";
    merged.audio = DEFAULTS.audio;
  }
  return merged;
}

let snapshot: Settings = load();

function emit() {
  for (const fn of listeners) fn();
}

export function useSettings(): {
  settings: Settings;
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  reset: () => void;
} {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  const set = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    snapshot = { ...snapshot, [key]: value };
    writeJSON(KEY, snapshot);
    emit();
  }, []);

  const reset = useCallback(() => {
    snapshot = { ...DEFAULTS };
    writeJSON(KEY, snapshot);
    emit();
  }, []);

  return { settings: snapshot, set, reset };
}
